<template>
    <div class="listening-note">
        <div class="note-container" :class="statusClass">
            <div class="status-indicator">
                <div class="status-left">
                    <div class="status-dot" :class="statusClass"></div>
                    <span class="status-text">{{ listeningStatusText }}</span>
                </div>
                <div class="pitch-feedback">
                    <PitchOffsetVisualizer
                        :cents-off="detectedPitch?.centsOff ?? 0"
                        :show-detected="shouldShowDetectedMarker"
                    />
                </div>
            </div>

            <!-- Separate feedback area - always present to avoid layout shift -->
            <div class="feedback-display">
                <span v-if="feedbackText && feedbackText[0] === 'B'">{{ feedbackText }}</span>
            </div>

            <div class="note-display">
                <MusicNote
                    :note="targetNote"
                    :clef="clef"
                    :duration="duration"
                    :width="noteWidth"
                    :height="noteHeight"
                    :overlay-note="wrongNoteOverlay"
                    overlay-color="#dc3545"
                />
            </div>

            <div v-if="showFingering" class="fingering-display">
                <FluteFingering
                    :note="showNoteLabel ? targetNote : undefined"
                    :fingering="fingering"
                    :width="fingeringWidth"
                    :height="fingeringHeight"
                    orientation="horizontal"
                />
            </div>
        </div>

        <div v-if="!hideControls" class="controls">
            <button @click="toggleListening" class="listen-button">
                {{ isListening ? "Stop Listening" : "Start Listening" }}
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onUnmounted, watch } from "vue";
import MusicNote from "./MusicNote.vue";
import FluteFingering from "./FluteFingering.vue";
import PitchOffsetVisualizer from "./PitchOffsetVisualizer.vue";
import {
    getStandardFingering,
    type FingeringState,
} from "../data/fluteFingeringChart";
import {
    createPitchDetector,
    DetectorType,
    analyzeFrequency,
    type PitchDetectionResult,
} from "../pitch";
import { analyzeNoteError, type NoteError } from "../pitch/feedbackAnalysis";

interface Props {
    targetNote: string;
    clef?: string;
    duration?: string;
    noteWidth?: number;
    noteHeight?: number;
    fingeringWidth?: number;
    fingeringHeight?: number;
    fingering?: FingeringState;
    showFingering?: boolean;
    showNoteLabel?: boolean;
    toleranceCents?: number;
    autoStart?: boolean;
    hideControls?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    clef: "treble",
    duration: "quarter",
    noteWidth: 200,
    noteHeight: 120,
    fingeringWidth: 500,
    fingeringHeight: 180,
    showFingering: true,
    showNoteLabel: true,
    toleranceCents: 50,
    autoStart: false,
    hideControls: false,
});

// Emit status changes for parent components
const emit = defineEmits<{
    "status-change": [
        status: string,
        detectedPitch: PitchDetectionResult | null,
        noteError: NoteError | null,
    ];
}>();

// Audio and detection state
const audioContext = ref<AudioContext | null>(null);
const analyser = ref<AnalyserNode | null>(null);
const mediaStream = ref<MediaStream | null>(null);
const detector = createPitchDetector(DetectorType.BLUESHARP, "flute");
const isListening = ref(false);
const animationFrameId = ref<number | null>(null);

// Pitch detection results
const detectedPitch = ref<PitchDetectionResult | null>(null);
const status = ref<"idle" | "listening" | "correct" | "close" | "wrong">(
    "idle",
);
const noteErrorAnalysis = ref<NoteError | null>(null);

// Track initial pitch to ignore carryover from previous card
const initialDetectedNote = ref<string | null>(null);
const acceptingInput = ref(false);
const hasCheckedInitialState = ref(false);
const analysisFrameCount = ref(0);
const MIN_FRAMES_BEFORE_CHECK = 10; // Wait ~166ms (10 frames @ 60fps) for buffer to populate

// Debouncing to reduce UI flicker
const statusDebounceTimeout = ref<number | null>(null);
const DEBOUNCE_MS = 150;

/**
 * Set status with selective debouncing to reduce flicker while keeping feedback responsive
 * - Immediate update for positive detections (correct, close, wrong)
 * - Debounce only "listening" state to prevent flicker from brief signal dropouts
 */
function setStatusDebounced(
    newStatus: "idle" | "listening" | "correct" | "close" | "wrong",
) {
    // Positive detections get immediate feedback
    if (
        newStatus === "correct" ||
        newStatus === "close" ||
        newStatus === "wrong"
    ) {
        // Clear any pending debounce
        if (statusDebounceTimeout.value !== null) {
            clearTimeout(statusDebounceTimeout.value);
            statusDebounceTimeout.value = null;
        }
        // Update immediately
        status.value = newStatus;
        return;
    }

    // Debounce "listening" state (no detection) to prevent flicker from brief signal loss
    if (newStatus === "listening") {
        // Only start debounce if not already debouncing
        // This prevents the timer from being reset every frame (60fps)
        if (statusDebounceTimeout.value === null) {
            statusDebounceTimeout.value = setTimeout(() => {
                status.value = newStatus;
                statusDebounceTimeout.value = null;
            }, DEBOUNCE_MS) as unknown as number;
        }
        // If already debouncing, do nothing - let the timer complete
        return;
    }

    // Idle always immediate
    status.value = newStatus;
}

// Get fingering from props or use standard fingering for the note
const fingering = computed(() => {
    if (props.fingering) {
        return props.fingering;
    }
    return getStandardFingering(props.targetNote) || {};
});

// Status display
// Listening status - stable, shows mic/detection status
const listeningStatusText = computed(() => {
    if (!isListening.value) {
        return "Not listening";
    }
    return "Listening...";
});

// Feedback text - shows actionable feedback only when needed
const feedbackText = computed(() => {
    // Use smart feedback if available
    if (noteErrorAnalysis.value) {
      return noteErrorAnalysis.value.feedback;
    }

    return "";
});

const statusClass = computed(() => `status-${status.value}`);

// Show detected marker whenever we have the correct or close note
const shouldShowDetectedMarker = computed(() => {
    return (
        detectedPitch.value &&
        detectedPitch.value.centsOff !== undefined &&
        (status.value === "correct" || status.value === "close")
    );
});

// Show wrong note overlay when status is "wrong"
const wrongNoteOverlay = computed(() => {
    if (status.value === "wrong" && detectedPitch.value?.note) {
        return detectedPitch.value.note;
    }
    return undefined;
});

async function startListening() {
    try {
        // Request microphone access
        mediaStream.value = await navigator.mediaDevices.getUserMedia({
            audio: {
                echoCancellation: false,
                noiseSuppression: false,
                autoGainControl: false,
            },
        });

        // Create audio context
        audioContext.value = new AudioContext();
        const source = audioContext.value.createMediaStreamSource(
            mediaStream.value,
        );

        // Create analyzer
        analyser.value = audioContext.value.createAnalyser();
        analyser.value.fftSize = 2048;
        analyser.value.smoothingTimeConstant = 0.8;
        source.connect(analyser.value);

        isListening.value = true;
        status.value = "listening";

        // Start analysis loop
        analyzePitch();
    } catch (error) {
        console.error("Failed to access microphone:", error);
        alert("Could not access microphone. Please check permissions.");
    }
}

function stopListening() {
    isListening.value = false;
    status.value = "idle";
    detectedPitch.value = null;

    // Cancel animation frame
    if (animationFrameId.value !== null) {
        cancelAnimationFrame(animationFrameId.value);
        animationFrameId.value = null;
    }

    // Stop media stream
    if (mediaStream.value) {
        mediaStream.value.getTracks().forEach((track) => track.stop());
        mediaStream.value = null;
    }

    // Close audio context
    if (audioContext.value) {
        audioContext.value.close();
        audioContext.value = null;
    }

    analyser.value = null;
}

function toggleListening() {
    if (isListening.value) {
        stopListening();
    } else {
        startListening();
    }
}

function analyzePitch() {
    if (!isListening.value || !analyser.value || !audioContext.value) {
        return;
    }

    // Increment frame counter
    analysisFrameCount.value++;

    // Skip first few frames to let audio buffer populate
    if (analysisFrameCount.value < MIN_FRAMES_BEFORE_CHECK) {
        animationFrameId.value = requestAnimationFrame(analyzePitch);
        return;
    }

    // Get audio data
    const buffer = new Float32Array(analyser.value.frequencyBinCount);
    analyser.value.getFloatTimeDomainData(buffer);

    // Detect pitch
    const result = detector.detectPitch(buffer, audioContext.value.sampleRate);

    if (result.frequency) {
        detectedPitch.value = result;

        // Analyze the detected frequency
        const analysis = analyzeFrequency(result.frequency);

        // Use smart feedback analysis
        const errorAnalysis = analyzeNoteError(
            analysis.noteName,
            props.targetNote,
            result,
            props.toleranceCents,
        );

        noteErrorAnalysis.value = errorAnalysis;

        // Ignore initial pitch detected when component first starts listening
        if (!acceptingInput.value) {
            if (!hasCheckedInitialState.value) {
                // First analysis cycle - store whatever we detect
                initialDetectedNote.value = analysis.noteName;
                hasCheckedInitialState.value = true;
                // Don't process this pitch, but continue the loop
            } else if (analysis.noteName !== initialDetectedNote.value) {
                // Different note detected - start accepting input
                acceptingInput.value = true;
                // Now process this new pitch
            } else {
                // Still the same initial note - ignore
                // Don't process, but continue the loop
            }
        }

        // Only process status if accepting input
        if (acceptingInput.value) {
            // Map error type to status for visual feedback (with debouncing)
            if (errorAnalysis.type === "correct") {
                setStatusDebounced("correct");
            } else if (
                errorAnalysis.type === "close" ||
                errorAnalysis.type === "wrong-octave"
            ) {
                setStatusDebounced("close");
            } else {
                // "wrong-note" shows as "wrong" status
                setStatusDebounced("wrong");
            }
        }
    } else {
        // No pitch detected (silence)
        detectedPitch.value = null;
        noteErrorAnalysis.value = null;

        // Handle silence cases
        if (!acceptingInput.value) {
            if (!hasCheckedInitialState.value) {
                // First analysis cycle detected silence
                hasCheckedInitialState.value = true;
                acceptingInput.value = true;
            } else if (initialDetectedNote.value !== null) {
                // Detected initial note, now silence - accept input
                acceptingInput.value = true;
            }
        }

        setStatusDebounced("listening");
    }

    // Continue analysis loop
    animationFrameId.value = requestAnimationFrame(analyzePitch);
}

// Watch for target note changes
watch(
    () => props.targetNote,
    () => {
        // Reset status when target note changes (new card)
        if (isListening.value) {
            status.value = "listening";
            detectedPitch.value = null;
        }
        // Reset input acceptance state for new card
        initialDetectedNote.value = null;
        acceptingInput.value = false;
        hasCheckedInitialState.value = false;
        analysisFrameCount.value = 0;
    },
);

// Watch for status changes and emit them
watch([status, detectedPitch, noteErrorAnalysis], () => {
    emit(
        "status-change",
        status.value,
        detectedPitch.value,
        noteErrorAnalysis.value,
    );
});

// Auto-start if requested
if (props.autoStart) {
    startListening();
}

// Cleanup on unmount
onUnmounted(() => {
    stopListening();

    // Clear any pending debounce timeout
    if (statusDebounceTimeout.value !== null) {
        clearTimeout(statusDebounceTimeout.value);
        statusDebounceTimeout.value = null;
    }
});
</script>

<style scoped>
.listening-note {
    display: flex;
    flex-direction: column;
    gap: 16px;
    min-width: 550px;
    max-width: 600px;
    margin: 0 auto;
}

.note-container {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 20px;
    background: white;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    border: 3px solid transparent;
    transition: all 0.3s ease;
}

.note-container.status-listening {
    border-color: #6c757d;
}

.note-container.status-correct {
    border-color: #28a745;
    /*background: linear-gradient(to bottom, #f0fff4, white);*/
}

.note-container.status-close {
    border-color: #ffc107;
    /*background: linear-gradient(to bottom, #fffbf0, white);*/
}

.note-container.status-wrong {
    border-color: #dc3545;
    /*background: linear-gradient(to bottom, #fff5f5, white);*/
}

.status-indicator {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 8px 12px;
    background: rgba(0, 0, 0, 0.03);
    border-radius: 6px;
    min-width: 500px;
}

.status-left {
    display: flex;
    align-items: center;
    gap: 10px;
}

.pitch-feedback {
    display: flex;
    align-items: center;
}

.status-dot {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    transition: all 0.3s ease;
}

.status-dot.status-idle {
    background: #adb5bd;
}

.status-dot.status-listening {
    background: #6c757d;
    animation: pulse 2s ease-in-out infinite;
}

.status-dot.status-correct {
    background: #28a745;
    box-shadow: 0 0 10px rgba(40, 167, 69, 0.5);
}

.status-dot.status-close {
    background: #ffc107;
}

.status-dot.status-wrong {
    background: #dc3545;
}

@keyframes pulse {
    0%,
    100% {
        opacity: 1;
        transform: scale(1);
    }
    50% {
        opacity: 0.5;
        transform: scale(1.2);
    }
}

.feedback-display {
    text-align: center;
    font-size: 16px;
    font-weight: 600;
    color: #0056b3;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.status-text {
    font-weight: 600;
    font-size: 14px;
    color: #495057;
}

.note-display {
    display: flex;
    justify-content: center;
}

.fingering-display {
    display: flex;
    justify-content: center;
}

.controls {
    display: flex;
    justify-content: center;
}

.listen-button {
    padding: 12px 24px;
    font-size: 16px;
    font-weight: 600;
    color: white;
    background: #007bff;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 2px 4px rgba(0, 123, 255, 0.3);
}

.listen-button:hover {
    background: #0056b3;
    box-shadow: 0 4px 8px rgba(0, 123, 255, 0.4);
    transform: translateY(-1px);
}

.listen-button:active {
    transform: translateY(0);
    box-shadow: 0 1px 2px rgba(0, 123, 255, 0.3);
}
</style>
